// src/components/singalong/SingAlongPlayer.js
//
// What a student or teacher sees in the Music popup when a topic has a
// published sing-along: the song player plus the lyrics, a page at a time,
// with the current line lit as it is sung.
//
// FULL SCREEN is there for the smart board: one click and the lyrics fill
// the classroom screen, with the play controls still at the bottom.
//
// "Show all lyrics" switches back to the plain full list, for a teacher who
// wants to read the whole song before playing it.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LyricsDisplay from "./LyricsDisplay";
import {
  cleanCues,
  endAtOf,
  fadeLevelAt,
  fileFadesItself,
  logoOpacityOf,
  parseLyrics,
  stopTimeOf,
} from "../../services/lyricSyncService";
import "./singalong.css";

const SingAlongPlayer = ({ musicUrl, sync, title, onEnded }) => {
  const audioRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(0);
  const [time, setTime] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [isFull, setIsFull] = useState(false);

  const parsed = useMemo(() => parseLyrics(sync?.lines || []), [sync]);
  const cues = useMemo(() => cleanCues(sync?.cues, parsed), [sync, parsed]);

  // "End sing-along here": the screen and the song fade out over 5 seconds
  // from the marker, then the song stops and counts as finished.
  const endAt = useMemo(() => endAtOf(sync), [sync]);
  const stopAt = stopTimeOf(endAt);
  const endedRef = useRef(false);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  const finishSong = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    // The "Music Star" popup lives outside this element, so it would be
    // hidden behind full screen. Leave full screen first.
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    if (onEndedRef.current) onEndedRef.current();
  }, []);

  // Applies the fade to the sound and stops at the end of it.
  const applyEnding = useCallback(
    (a) => {
      if (!a || stopAt === null) return;
      if (a.currentTime >= stopAt) {
        a.pause();
        a.volume = 1;
        finishSong();
        return;
      }
      // The faded MP3 already fades its own sound — only the screen fades.
      a.volume = fileFadesItself(a.duration, stopAt) ? 1 : fadeLevelAt(endAt, a.currentTime);
    },
    [endAt, stopAt, finishSong]
  );

  // While playing, follow the audio every animation frame; timeupdate alone
  // fires only ~4 times a second, which makes the word highlight stutter.
  const tick = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      setTime(a.currentTime);
      applyEnding(a);
    }
    if (a && !a.paused && !a.ended) rafRef.current = requestAnimationFrame(tick);
  }, [applyEnding]);

  const startLoop = () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  useEffect(() => {
    const onChange = () => setIsFull(document.fullscreenElement === wrapRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFull = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await wrapRef.current?.requestFullscreen?.();
    } catch {
      // Some kiosk browsers refuse full screen; the player still works.
    }
  };

  return (
    <div className={`sa-player ${isFull ? "is-full" : ""}`} ref={wrapRef}>
      {showAll ? (
        <div className="sa-all-lyrics">
          {parsed.lines.map((line, idx) =>
            String(line).trim() === "" ? (
              <div key={idx} className="sa-all-break" aria-hidden="true" />
            ) : (
              <p key={idx} className="sa-all-line">
                {line}
              </p>
            )
          )}
        </div>
      ) : (
        <LyricsDisplay
          parsed={parsed}
          cues={cues}
          time={time}
          themeKey={sync?.theme}
          title={title}
          logoOpacity={logoOpacityOf(sync)}
          fade={fadeLevelAt(endAt, time)}
        />
      )}

      <div className="sa-controls">
        <audio
          ref={audioRef}
          controls
          src={musicUrl}
          className="sa-audio"
          onPlay={() => {
            const a = audioRef.current;
            // Pressing play again from the end starts the song over.
            if (a && stopAt !== null && a.currentTime >= stopAt - 0.05) a.currentTime = 0;
            if (a && stopAt !== null && a.currentTime < (endAt ?? 0)) endedRef.current = false;
            if (a) a.volume = fileFadesItself(a.duration, stopAt) ? 1 : fadeLevelAt(endAt, a.currentTime);
            startLoop();
          }}
          onSeeked={() => {
            const a = audioRef.current;
            setTime(a?.currentTime || 0);
            if (a && stopAt !== null) {
              if (a.currentTime < (endAt ?? 0)) endedRef.current = false;
              a.volume = fileFadesItself(a.duration, stopAt) ? 1 : fadeLevelAt(endAt, a.currentTime);
            }
          }}
          onTimeUpdate={() => {
            const a = audioRef.current;
            // Keeps the screen right when paused-and-scrubbed.
            if (a && a.paused) setTime(a.currentTime);
            // Backup for the ending if animation frames are paused.
            if (a && !a.paused) applyEnding(a);
          }}
          onEnded={() => {
            setTime(audioRef.current?.currentTime || 0);
            endedRef.current = false;
            finishSong();
          }}
          controlsList="nodownload noplaybackrate noremoteplayback"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
        />
        <div className="sa-control-btns">
          <button type="button" className="sa-chip" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "🎤 Sing-along" : "📄 Show all lyrics"}
          </button>
          <button type="button" className="sa-chip" onClick={toggleFull}>
            {isFull ? "✕ Exit full screen" : "⛶ Full screen"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SingAlongPlayer;
